/**
 * The case chain on the REPORT page (admin-only) — resolves the
 * report's codename family and renders the horizontal chain with this
 * report as the current node; sibling chips navigate into
 * /cases/{family}?item=…. Renders nothing when the report resolves no
 * family (self-served legacy rows) or the caller isn't an admin.
 */

import { useEffect, useState } from 'react';

import { vnextApiClient } from '@/services/vnext-api';
import type { CaseDetail } from '@/types/case';
import { CaseChain } from './CaseChain';

export function ReportCaseChain({ reportId }: Readonly<{ reportId: string }>) {
  const [caseDetail, setCaseDetail] = useState<CaseDetail | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCaseDetail(null);
    void (async () => {
      try {
        const family = await vnextApiClient.resolveCase('report', reportId);
        const detail = await vnextApiClient.getCase(family);
        if (!cancelled) {
          setCaseDetail(detail);
        }
      } catch {
        // No chain is better than a broken one.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  if (!caseDetail) {
    return null;
  }

  return <CaseChain caseDetail={caseDetail} current={{ type: 'report', id: reportId }} />;
}
